import React from 'react'
import PropTypes from 'prop-types'
import { Edit2, Copy, Trash2, Shield, Users, Lock } from 'lucide-react'
import { Modal, Button, Alert } from '@components/common'
import './CompanyRoles.scss'

/**
 * Check if a role is a system default role
 * Handles string "1"/"0", number 1/0, and boolean true/false
 */
const checkIsSystemRole = (role) => {
	const value = role?.is_system_default
	return value === true || value === 1 || value === '1'
}

/**
 * CompanyRoleView Component
 *
 * Modal for viewing role details
 * System roles show a notice that they cannot be edited
 *
 * @param {Object} props
 * @param {Object} props.roleData - Role data to display
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onEdit - Edit handler
 * @param {Function} props.onClone - Clone handler
 * @param {Function} props.onDelete - Delete handler
 */
const CompanyRoleView = ({ roleData, onClose, onEdit, onClone, onDelete }) => {
	if (!roleData) return null

	const isSystemRole = checkIsSystemRole(roleData)
	const isArchived = roleData.status === 'archived' || roleData.deleted_at

	// Parse permissions - handle both string and object formats
	let permissions = {}
	try {
		if (typeof roleData.permissions === 'string') {
			// Try to parse JSON string
			permissions = JSON.parse(roleData.permissions || '{}')
		} else if (typeof roleData.permissions === 'object' && roleData.permissions !== null) {
			permissions = roleData.permissions
		}
	} catch (error) {
		console.error('Failed to parse permissions:', error)
		permissions = {}
	}

	// Additional check: ensure permissions is an object, not a string
	if (typeof permissions === 'string') {
		// Double-encoded JSON - parse again
		try {
			permissions = JSON.parse(permissions)
		} catch (error) {
			console.error('Failed to parse double-encoded permissions:', error)
			permissions = {}
		}
	}

	/**
	 * Get status badge
	 */
	const getStatusBadge = () => {
		if (isArchived) {
			return <span className="badge badge--archived">Archived</span>
		}
		return <span className="badge badge--active">Active</span>
	}

	/**
	 * Get type badge
	 */
	const getTypeBadge = () => {
		if (isSystemRole) {
			return (
				<span className="badge badge--primary">
					<Lock size={12} />
					System Default
				</span>
			)
		}
		return <span className="badge badge--info">Custom Role</span>
	}

	return (
		<Modal
			isOpen={true}
			onClose={onClose}
			size="large"
			closeOnBackdrop={false}
			title={
				<div className="role-view__header-content">
					<div>
						<div className="role-view__title">{roleData.name}</div>
						<div className="role-view__badges">
							{getTypeBadge()}
							{getStatusBadge()}
						</div>
					</div>
				</div>
			}
			footer={
				<>
					<Button variant="secondary" onClick={onClose}>
						Close
					</Button>
					<div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
						{!isArchived && (
							<Button variant="info" onClick={onClone} icon={<Copy size={16} />}>
								Clone
							</Button>
						)}
						{!isSystemRole && !isArchived && (
							<>
								<Button variant="primary" onClick={onEdit} icon={<Edit2 size={16} />}>
									Edit
								</Button>
								<Button variant="danger" onClick={onDelete} icon={<Trash2 size={16} />}>
									Archive
								</Button>
							</>
						)}
					</div>
				</>
			}
		>
			<div className="role-view">
				{/* System Role Notice */}
				{isSystemRole && (
					<Alert
						variant="info"
						className="role-view__system-notice"
						message="This is a system role and cannot be modified. You can clone it to create a customizable version."
						dismissible={false}
					/>
				)}

				{/* Basic Information */}
				<div className="role-view__section">
					<h3 className="role-view__section-title">
						<Shield size={20} />
						Role Information
					</h3>
					<div className="role-view__grid">
						<div className="role-view__field">
							<label>Role Name</label>
							<p>{roleData.name}</p>
						</div>
						<div className="role-view__field">
							<label>Slug</label>
							<p className="role-view__slug">{roleData.slug}</p>
						</div>
						{roleData.description && (
							<div className="role-view__field role-view__field--full">
								<label>Description</label>
								<p>{roleData.description}</p>
							</div>
						)}
					</div>
				</div>

				{/* Permissions */}
				{permissions && typeof permissions === 'object' && Object.keys(permissions).length > 0 && (
					<div className="role-view__section">
						<h3 className="role-view__section-title">
							<Users size={20} />
							Permissions ({Object.keys(permissions).length} {Object.keys(permissions).length === 1 ? 'resource' : 'resources'})
						</h3>
						<div className="role-view__permissions">
							{/* Check for wildcard/full access permissions */}
							{permissions['*'] && Array.isArray(permissions['*']) && permissions['*'].includes('*') ? (
								<div className="role-view__full-access">
									<span className="badge badge--success">Full Access</span>
									<p>This role has unrestricted access to all resources and actions across the entire system.</p>
								</div>
							) : (
								Object.entries(permissions).map(([resource, actions]) => (
									<div key={resource} className="role-view__permission-group">
										<h4 className="role-view__permission-resource">{resource}</h4>
										<div className="role-view__permission-actions">
											{Array.isArray(actions) ? (
												actions.map((action, index) => (
													<span key={`${resource}-${action}-${index}`} className="badge badge--secondary">
														{action}
													</span>
												))
											) : (
												<span className="badge badge--secondary">{String(actions)}</span>
											)}
										</div>
									</div>
								))
							)}
						</div>
					</div>
				)}

				{/* No Permissions Notice */}
				{(!permissions || Object.keys(permissions).length === 0) && (
					<div className="role-view__section">
						<h3 className="role-view__section-title">
							<Users size={20} />
							Permissions
						</h3>
						<Alert
							variant="warning"
							message={`No permissions configured for this role.${!isSystemRole ? ' Edit the role to add permissions.' : ''}`}
							dismissible={false}
						/>
					</div>
				)}

				{/* Metadata */}
				<div className="role-view__section">
					<h3 className="role-view__section-title">Metadata</h3>
					<div className="role-view__grid">
						{roleData.created_at && (
							<div className="role-view__field">
								<label>Created</label>
								<p>{new Date(roleData.created_at).toLocaleDateString()}</p>
							</div>
						)}
						{roleData.updated_at && (
							<div className="role-view__field">
								<label>Last Updated</label>
								<p>{new Date(roleData.updated_at).toLocaleDateString()}</p>
							</div>
						)}
						{roleData.deleted_at && (
							<div className="role-view__field">
								<label>Archived</label>
								<p>{new Date(roleData.deleted_at).toLocaleDateString()}</p>
							</div>
						)}
					</div>
				</div>
			</div>
		</Modal>
	)
}

CompanyRoleView.propTypes = {
	roleData: PropTypes.shape({
		id: PropTypes.number,
		name: PropTypes.string,
		slug: PropTypes.string,
		description: PropTypes.string,
		permissions: PropTypes.object,
		is_system_default: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
		status: PropTypes.string,
		deleted_at: PropTypes.string,
		created_at: PropTypes.string,
		updated_at: PropTypes.string,
	}).isRequired,
	onClose: PropTypes.func.isRequired,
	onEdit: PropTypes.func.isRequired,
	onClone: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired,
}

export default CompanyRoleView
